import React from "react";
import { connect } from "react-redux";

import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import SettingPath from "@app/enum/settings/SettingPath";
import settingsAnalytics from "@app/scene/settings/settingsAnaytics";
import {
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  isCurrentUserOwner,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";

import ActivityTypes from "./component/ActivityTypes";
import CalendarSync from "./component/CalendarSync";
import EmailPreferences from "./component/EmailPreferences";
import FileTags from "./component/FileTags";
import FormLayouts from "./component/FormLayouts";
import { ImportHistory, NewImport } from "./component/Imports";
import { getImportHistory } from "./component/Imports/store/selectors";
import Integrations from "./component/Integrations";
import ManageFields from "./component/ManageFields";
import MapPreferences from "./component/MapPreferences";
import MergeDuplicates from "./component/MergeDuplicates";
import Notifications from "./component/Notifications";
import OrganizationDetails from "./component/OrganizationDetails";
import PinnedFields from "./component/PinnedFields";
import Profile from "./component/Profile";
import RestorableRecords from "./component/RestorableRecords";
import Sandbox from "./component/Sandbox";

interface Props {
  doesUserHaveAccessToDupeCheck: boolean;
  doesUserHaveAccessToImports: boolean;
  isOwner: boolean;
}

const SettingsPage: React.FC<Props> = ({
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  isOwner,
}) => {
  const { path } = useRouteMatch();

  return (
    <AnalyticsContext.Provider value={settingsAnalytics}>
      <Switch>
        <Route exact path={`${path}/${SettingPath.PERSONAL_PROFILE}`} component={Profile} />
        <Route
          exact
          path={`${path}/${SettingPath.PERSONAL_EMAIL_PREFERENCES}`}
          component={EmailPreferences}
        />
        <Route
          exact
          path={`${path}/${SettingPath.PERSONAL_MAP_PREFERENCES}`}
          component={MapPreferences}
        />
        <Route
          exact
          path={`${path}/${SettingPath.PERSONAL_CALENDAR_SYNC}`}
          component={CalendarSync}
        />
        <Route exact path={path}>
          <Redirect to={`${path}/${SettingPath.PERSONAL_PROFILE}`} />
        </Route>
        {isOwner && (
          <Route
            path={`${path}/${SettingPath.ORGANIZATION_INTEGRATIONS}`}
            component={Integrations}
          />
        )}
        {isOwner && (
          <Route path={`${path}/${SettingPath.ORGANIZATION_SANDBOX}`} component={Sandbox} />
        )}
        {doesUserHaveAccessToImports && (
          <Route path={`${path}/${SettingPath.DATA_IMPORT_HISTORY}`} component={ImportHistory} />
        )}
        {doesUserHaveAccessToImports && (
          <Route path={`${path}/${SettingPath.DATA_NEW_IMPORT}`} component={NewImport} />
        )}
        {isOwner && (
          <Route
            exact
            path={`${path}/${SettingPath.ORGANIZATION_DETAILS}/:tab?`}
            component={OrganizationDetails}
          />
        )}
        {isOwner && (
          <Route exact path={`${path}/${SettingPath.ORGANIZATION_ACTIVITY_TYPES}`}>
            <ActivityTypes />
          </Route>
        )}
        {isOwner && (
          <Route
            exact
            path={`${path}/${SettingPath.FORMS_MANAGE_FIELDS}/:tab?/:id?`}
            component={ManageFields}
          />
        )}
        <Route exact path={`${path}/${SettingPath.PINNED_FIELDS}`} component={PinnedFields} />
        <Route exact path={`${path}/${SettingPath.FORMS_FILE_TAGS}`} component={FileTags} />
        {isOwner && (
          <Route
            exact
            path={`${path}/${SettingPath.FORMS_CREATE}/:tab?/:id?`}
            component={FormLayouts}
          />
        )}
        {doesUserHaveAccessToDupeCheck && (
          <Route
            path={`${path}/${SettingPath.FORMS_MERGE_DUPLICATES}`}
            component={MergeDuplicates}
          />
        )}
        <Route
          path={`${path}/${SettingPath.DATA_RESTORABLE_RECORDS}`}
          component={RestorableRecords}
        />
        <Route path={`${path}/${SettingPath.PRIVACY_NOTIFICATIONS}`} component={Notifications} />
      </Switch>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  doesUserHaveAccessToDupeCheck: doesUserHaveAccessToDupeCheck(state),
  doesUserHaveAccessToImports: doesUserHaveAccessToImports(state),
  importHistory: getImportHistory(state),
  isOwner: isCurrentUserOwner(state),
});

export default connect(mapStateToProps)(SettingsPage);
